import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { format, startOfWeek, startOfMonth, endOfWeek, endOfMonth, parse } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Download, LogOut, BarChart as ChartIcon, Users, Truck, Flag, Calendar, X, Settings, CreditCard as Edit2, MapPin, Menu, Upload } from 'lucide-react';
import { Registration, Firm } from '../types';
import { Link } from 'react-router-dom';
import HeatMap from '../components/HeatMap';
import LicensePlateMap from '../components/LicensePlateMap';
import { useLoadScript } from '@react-google-maps/api';

const libraries: ("places" | "visualization" | "drawing" | "geometry" | "localContext" | "marker")[] = ["visualization", "places", "marker"];

interface RegistrationModalProps {
  registrations: Registration[];
  isOpen: boolean;
  onClose: () => void;
  title: string;
}

const RegistrationModal: React.FC<RegistrationModalProps> = ({ registrations, isOpen, onClose, title }) => {
  if (!isOpen) return null;

  const sortedRegistrations = [...registrations].sort((a, b) => b.timestamp.seconds - a.timestamp.seconds);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="p-6 overflow-auto max-h-[calc(90vh-120px)]">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reg.nr
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Land
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Selskap
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Posisjon
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedRegistrations.map((reg, index) => (
                <tr key={`${reg.licensePlate}-${reg.timestamp.seconds}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {format(new Date(reg.timestamp.seconds * 1000), 'dd.MM.yyyy HH:mm')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{reg.licensePlate}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{reg.countryCode}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{reg.busCompany}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {reg.location.latitude.toFixed(4)}, {reg.location.longitude.toFixed(4)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { signOut } = useAuth();
  const { isLoaded: mapsLoaded, loadError: mapsLoadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries,
    mapIds: [import.meta.env.VITE_GOOGLE_MAPS_MAP_ID]
  });
  
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [firms, setFirms] = useState<Firm[]>([]);
  const [timeFrame, setTimeFrame] = useState<'week' | 'month' | 'year' | 'all' | 'custom'>('all');
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [loading, setLoading] = useState(true);
  const [modalData, setModalData] = useState<{ registrations: Registration[]; title: string } | null>(null);
  const [selectedLicensePlate, setSelectedLicensePlate] = useState<string>('');
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const registrationsSnapshot = await getDocs(collection(db, 'registrations'));
        const firmsSnapshot = await getDocs(collection(db, 'firms'));
        
        const registrationsData = registrationsSnapshot.docs.map(doc => doc.data() as Registration);
        const firmsData = firmsSnapshot.docs.map(doc => doc.data() as Firm);
        
        setRegistrations(registrationsData);
        setFirms(firmsData);
        setLoading(false);
      } catch (error) {
        console.error('Error ved henting av data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filterRegistrationsByTime = (registrations: Registration[]) => {
    if (timeFrame === 'all') return registrations;

    const now = new Date();
    let start: Date;
    let end: Date;

    switch (timeFrame) {
      case 'week':
        start = startOfWeek(now);
        end = endOfWeek(now);
        break;
      case 'month':
        start = startOfMonth(now);
        end = endOfMonth(now);
        break;
      case 'year':
        start = new Date(now.getFullYear(), 0, 1); // January 1st of current year
        end = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999); // December 31st of current year
        break;
      case 'custom':
        const selectedDate = parse(selectedMonth, 'yyyy-MM', new Date());
        start = startOfMonth(selectedDate);
        end = endOfMonth(selectedDate);
        break;
      default:
        return registrations;
    }

    return registrations.filter(reg => {
      const regDate = new Date(reg.timestamp.seconds * 1000);
      return regDate >= start && regDate <= end;
    });
  };

  const getRegistrationStats = () => {
    const filteredRegistrations = filterRegistrationsByTime(registrations);
    
    const countryStats = filteredRegistrations.reduce((acc, reg) => {
      acc[reg.countryCode] = (acc[reg.countryCode] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const firmStats = filteredRegistrations.reduce((acc, reg) => {
      acc[reg.busCompany] = (acc[reg.busCompany] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const licensePlateStats = filteredRegistrations.reduce((acc, reg) => {
      acc[reg.licensePlate] = (acc[reg.licensePlate] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const uniqueUsers = new Set(filteredRegistrations.map(reg => reg.userId)).size;

    const topCountries = Object.entries(countryStats)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 20)
      .map(([country, count]) => ({ country, count }));

    const topCompanies = Object.entries(firmStats)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 20)
      .map(([company, count]) => ({ company, count }));

    const topLicensePlates = Object.entries(licensePlateStats)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 20)
      .map(([plate, count]) => ({
        plate,
        count,
        lastSeen: filteredRegistrations
          .filter(reg => reg.licensePlate === plate)
          .reduce((latest, reg) => {
            const regDate = new Date(reg.timestamp.seconds * 1000);
            return latest > regDate ? latest : regDate;
          }, new Date(0))
      }));

    return {
      total: filteredRegistrations.length,
      uniqueUsers,
      byCountry: countryStats,
      byFirm: firmStats,
      byLicensePlate: licensePlateStats,
      topLicensePlates,
      topCountries,
      topCompanies
    };
  };

  // Set default selected license plate to the most registered one
  React.useEffect(() => {
    const stats = getRegistrationStats();
    if (stats.topLicensePlates.length > 0 && !selectedLicensePlate) {
      setSelectedLicensePlate(stats.topLicensePlates[0].plate);
    }
  }, [registrations, timeFrame, selectedMonth, selectedLicensePlate]);

  const handleRowClick = (type: 'country' | 'company' | 'plate', value: string) => {
    const filteredRegistrations = filterRegistrationsByTime(registrations).filter(reg => {
      switch (type) {
        case 'country':
          return reg.countryCode === value;
        case 'company':
          return reg.busCompany === value;
        case 'plate':
          return reg.licensePlate === value;
        default:
          return false;
      }
    });

    const titles = {
      country: `Registreringer fra ${value}`,
      company: `Registreringer for ${value}`,
      plate: `Registreringer for kjøretøy ${value}`
    };

    setModalData({
      registrations: filteredRegistrations,
      title: titles[type]
    });
  };

  const downloadCSV = () => {
    const filteredRegistrations = filterRegistrationsByTime(registrations);
    const headers = ['Dato', 'Registreringsnummer', 'Land', 'Busselskap', 'Posisjon', 'Merknader'];
    const csvContent = [
      headers.join(','),
      ...filteredRegistrations.map(reg => [
        format(new Date(reg.timestamp.seconds * 1000), 'yyyy-MM-dd HH:mm:ss'),
        reg.licensePlate,
        reg.countryCode,
        reg.busCompany,
        `${reg.location.latitude},${reg.location.longitude}`,
        reg.merknad
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const fileName = timeFrame === 'custom' 
      ? `registreringer_${selectedMonth}_${format(new Date(), 'yyyy-MM-dd')}.csv`
      : `registreringer_${timeFrame}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.download = fileName;
    link.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const stats = getRegistrationStats();
  const filteredRegistrations = filterRegistrationsByTime(registrations);

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <ChartIcon className="h-8 w-8 text-blue-600" />
              <h1 className="ml-2 text-xl font-semibold text-gray-900">Registreringsoversikt</h1>
            </div>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Menu className="h-4 w-4 mr-2" />
                  Administrer
                </button>
                
                {showDropdown && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowDropdown(false)}
                    ></div>
                    <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                      <div className="py-1" role="menu">
                        <Link
                          to="/manage-registrations"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setShowDropdown(false)}
                        >
                          <Edit2 className="h-4 w-4 mr-3" />
                          Registreringer
                        </Link>
                        <Link
                          to="/import-registrations"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setShowDropdown(false)}
                        >
                          <Upload className="h-4 w-4 mr-3" />
                          Importer registreringer
                        </Link>
                        <div className="border-t border-gray-100"></div>
                        <Link
                          to="/manage-companies"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setShowDropdown(false)}
                        >
                          <Settings className="h-4 w-4 mr-3" />
                          Busselskaper
                        </Link>
                        <Link
                          to="/manage-license-plates"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setShowDropdown(false)}
                        >
                          <Truck className="h-4 w-4 mr-3" />
                          Registreringsnummer
                        </Link>
                        <Link
                          to="/manage-countries"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setShowDropdown(false)}
                        >
                          <Flag className="h-4 w-4 mr-3" />
                          Land
                        </Link>
                        <Link
                          to="/manage-bus-companies"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setShowDropdown(false)}
                        >
                          <Users className="h-4 w-4 mr-3" />
                          Busselskaper (ny)
                        </Link>
                        <div className="border-t border-gray-100"></div>
                        <Link
                          to="/migration"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setShowDropdown(false)}
                        >
                          <Settings className="h-4 w-4 mr-3" />
                          Migrering og opprydding
                        </Link>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={() => signOut()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logg ut
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
              <div className="flex flex-col space-y-2">
                <h2 className="text-lg font-medium text-gray-900">Tidsperiode</h2>
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <select
                      value={timeFrame}
                      onChange={(e) => setTimeFrame(e.target.value as 'week' | 'month' | 'year' | 'all' | 'custom')}
                      className="appearance-none bg-white border border-gray-300 rounded-md pl-3 pr-10 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">Hele perioden</option>
                      <option value="week">Denne uken</option>
                      <option value="month">Denne måneden</option>
                      <option value="year">Dette året</option>
                      <option value="custom">Velg måned</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                      <Calendar className="h-4 w-4" />
                    </div>
                  </div>
                  
                  {timeFrame === 'custom' && (
                    <div className="flex items-center bg-blue-50 rounded-md p-2 border border-blue-200">
                      <Calendar className="h-5 w-5 text-blue-500 mr-2" />
                      <input
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        className="bg-transparent border-none focus:ring-0 text-blue-700 font-medium"
                      />
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={downloadCSV}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 shadow-sm transition-colors duration-150"
              >
                <Download className="h-4 w-4 mr-2" />
                Last ned CSV
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ChartIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Totalt antall registreringer</dt>
                      <dd className="text-3xl font-semibold text-gray-900">{stats.total}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Users className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Unike brukere</dt>
                      <dd className="text-3xl font-semibold text-gray-900">{stats.uniqueUsers}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Flag className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Land</dt>
                      <dd className="text-3xl font-semibold text-gray-900">
                        {Object.keys(stats.byCountry).length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Users className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Busselskaper</dt>
                      <dd className="text-3xl font-semibold text-gray-900">
                        {Object.keys(stats.byFirm).length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Truck className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Unike kjøretøy</dt>
                      <dd className="text-3xl font-semibold text-gray-900">
                        {Object.keys(stats.byLicensePlate).length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Topp 20 land</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Land
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Antall
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stats.topCountries.map((country, index) => (
                      <tr 
                        key={country.country}
                        onClick={() => handleRowClick('country', country.country)}
                        className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 cursor-pointer`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {country.country}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {country.count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Topp 20 selskaper</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Selskap
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Antall
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stats.topCompanies.map((company, index) => (
                      <tr 
                        key={company.company}
                        onClick={() => handleRowClick('company', company.company)}
                        className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 cursor-pointer`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {company.company}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {company.count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Topp 20 mest registrerte kjøretøy</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Registreringsnummer
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Antall
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sist sett
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stats.topLicensePlates.map((plate, index) => (
                      <tr 
                        key={plate.plate}
                        onClick={() => handleRowClick('plate', plate.plate)}
                        className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 cursor-pointer`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {plate.plate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {plate.count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {format(plate.lastSeen, 'dd.MM.yyyy HH:mm')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <MapPin className="h-6 w-6 text-blue-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">Kjøretøysporing på kart</h3>
                </div>
                <div className="flex items-center space-x-4">
                  <label className="text-sm font-medium text-gray-700">
                    Velg registreringsnummer:
                  </label>
                  <select
                    value={selectedLicensePlate}
                    onChange={(e) => setSelectedLicensePlate(e.target.value)}
                    className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Velg kjøretøy...</option>
                    {stats.topLicensePlates.map(plate => (
                      <option key={plate.plate} value={plate.plate}>
                        {plate.plate} ({plate.count} registreringer)
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              {selectedLicensePlate && mapsLoaded && !mapsLoadError ? (
                <div>
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-blue-900">
                          Registreringer for {selectedLicensePlate}
                        </h4>
                        <p className="text-sm text-blue-700">
                          Totalt {stats.byLicensePlate[selectedLicensePlate]} registreringer
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-blue-700">
                          Sist sett: {format(
                            stats.topLicensePlates.find(p => p.plate === selectedLicensePlate)?.lastSeen || new Date(),
                            'dd.MM.yyyy HH:mm'
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  {mapsLoaded && (
                    <LicensePlateMap 
                      registrations={filterRegistrationsByTime(registrations)} 
                      selectedPlate={selectedLicensePlate}
                      isLoaded={mapsLoaded}
                    />
                  )}
                  <div className="mt-4 text-sm text-gray-600">
                    <div className="flex items-center space-x-6">
                      <div className="flex items-center">
                        <div className="w-4 h-4 rounded-full bg-blue-500 mr-2"></div>
                        <span>1-2 registreringer</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-4 h-4 rounded-full bg-yellow-500 mr-2"></div>
                        <span>3-5 registreringer</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-4 h-4 rounded-full bg-red-600 mr-2"></div>
                        <span>6+ registreringer</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : selectedLicensePlate && mapsLoadError ? (
                <div className="h-[500px] flex items-center justify-center bg-red-50 rounded">
                  <div className="text-center">
                    <p className="text-red-600">Feil ved lasting av Google Maps</p>
                  </div>
                </div>
              ) : (
                <div className="h-[500px] flex items-center justify-center bg-gray-50 rounded">
                  <div className="text-center">
                    <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Velg et registreringsnummer for å se posisjoner på kartet</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {modalData && (
        <RegistrationModal
          registrations={modalData.registrations}
          isOpen={!!modalData}
          onClose={() => setModalData(null)}
          title={modalData.title}
        />
      )}
    </div>
  );
}